import { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import {
  createVendorAssessment,
  deleteVendorAssessment,
  fetchVendorAssessments,
  suggestVendorAssessmentNo,
  updateVendorAssessment,
} from '../../api/vendorAssessment';
import {
  VendorAssessment,
  VendorAssessmentCriteria,
  VendorAssessmentDocType,
  VendorAssessmentJudgment,
  VendorAssessmentPayload,
  VendorAssessmentPurpose,
  VendorAssessmentStatus,
  VendorEvalCriteria,
  VendorEvalItem,
  VendorReportCriteria,
  VendorSurveyCriteria,
  VendorSurveyItem,
  calcEvalScoreText,
  defaultCriteriaForDocType,
  parseAssessmentCriteria,
  vendorAssessmentDocHint,
  vendorAssessmentDocTypeLabel,
  vendorAssessmentJudgmentLabel,
  vendorAssessmentPurposeLabel,
  vendorAssessmentStatusLabel,
} from '../../types/vendorAssessment';

import { reportError } from '../../utils/notify';
type DocFilter = 'ALL' | VendorAssessmentDocType;
type Mode = 'list' | 'create' | 'edit';

type Props = {
  vendorId: number;
};

type FormState = Omit<VendorAssessmentPayload, 'vendorId' | 'criteriaJson'>;

const EMPTY_FORM: FormState = {
  docType: 'SURVEY',
  purpose: 'INITIAL',
  assessmentNo: '',
  assessmentDate: new Date().toISOString().slice(0, 10),
  title: '',
  scoreText: '',
  judgment: 'PENDING',
  summary: '',
  evaluator: '',
  status: 'DRAFT',
  remark: '',
};

function newItemId() {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

function defaultTitle(docType: VendorAssessmentDocType): string {
  switch (docType) {
    case 'SURVEY':
      return '협력업체 조사표';
    case 'EVALUATION':
      return '협력업체 평가표';
    case 'REPORT':
      return '협력업체 조사보고서';
    default:
      return '협력업체 조사/평가';
  }
}

function defaultPurpose(docType: VendorAssessmentDocType): VendorAssessmentPurpose {
  return docType === 'EVALUATION' ? 'PERIODIC' : 'INITIAL';
}

function personLabel(docType: VendorAssessmentDocType): string {
  return docType === 'SURVEY' ? '조사자' : docType === 'REPORT' ? '작성자' : '평가자';
}

function dateLabel(docType: VendorAssessmentDocType): string {
  return docType === 'SURVEY' ? '조사일' : docType === 'REPORT' ? '보고일' : '평가일';
}

function VendorAssessmentSection({ vendorId }: Props) {
  const [docFilter, setDocFilter] = useState<DocFilter>('ALL');
  const [rows, setRows] = useState<VendorAssessment[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [mode, setMode] = useState<Mode>('list');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<FormState>({ ...EMPTY_FORM });
  const [criteria, setCriteria] = useState<VendorAssessmentCriteria>(defaultSurveyLike());

  const docType = (form.docType as VendorAssessmentDocType) || 'SURVEY';
  const evalScorePreview = useMemo(() => {
    if (criteria.kind !== 'EVALUATION') return '';
    return calcEvalScoreText(criteria.items);
  }, [criteria]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      setRows(
        await fetchVendorAssessments(vendorId, docFilter === 'ALL' ? undefined : docFilter)
      );
    } catch {
      reportError(setError, '조사/평가 문서를 불러오지 못했습니다.');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [vendorId, docFilter]);

  useEffect(() => {
    void load();
  }, [load]);

  const openCreate = async (nextType: VendorAssessmentDocType = 'SURVEY') => {
    setMode('create');
    setEditingId(null);
    setError('');
    let assessmentNo = '';
    try {
      const suggestion = await suggestVendorAssessmentNo(nextType);
      assessmentNo = suggestion.suggestedValue || '';
    } catch {
      // ignore
    }
    setForm({
      ...EMPTY_FORM,
      docType: nextType,
      purpose: defaultPurpose(nextType),
      assessmentNo,
      assessmentDate: new Date().toISOString().slice(0, 10),
      title: defaultTitle(nextType),
    });
    setCriteria(defaultCriteriaForDocType(nextType));
  };

  const openEdit = (row: VendorAssessment) => {
    const type = (row.docType as VendorAssessmentDocType) || 'SURVEY';
    setMode('edit');
    setEditingId(row.assessmentId);
    setError('');
    setForm({
      docType: type,
      purpose: (row.purpose as VendorAssessmentPurpose) || defaultPurpose(type),
      assessmentNo: row.assessmentNo || '',
      assessmentDate: row.assessmentDate?.slice(0, 10) || '',
      title: row.title || '',
      scoreText: row.scoreText || '',
      judgment: (row.judgment as VendorAssessmentJudgment) || 'PENDING',
      summary: row.summary || '',
      evaluator: row.evaluator || '',
      status: (row.status as VendorAssessmentStatus) || 'DRAFT',
      remark: row.remark || '',
    });
    setCriteria(parseAssessmentCriteria(type, row.criteriaJson));
  };

  const cancelForm = () => {
    setMode('list');
    setEditingId(null);
    setForm({ ...EMPTY_FORM });
    setCriteria(defaultSurveyLike());
  };

  const patch = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleDocTypeChange = async (nextType: VendorAssessmentDocType) => {
    const prevType = docType;
    const prevTitle = form.title || '';
    patch('docType', nextType);
    if (!prevTitle.trim() || prevTitle === defaultTitle(prevType)) {
      patch('title', defaultTitle(nextType));
    }
    if (form.purpose === defaultPurpose(prevType)) {
      patch('purpose', defaultPurpose(nextType));
    }
    setCriteria(defaultCriteriaForDocType(nextType));
    if (mode === 'create') {
      try {
        const suggestion = await suggestVendorAssessmentNo(nextType);
        patch('assessmentNo', suggestion.suggestedValue || '');
      } catch {
        // keep
      }
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError('');
      const scoreText =
        criteria.kind === 'EVALUATION'
          ? calcEvalScoreText(criteria.items) || (form.scoreText || '').trim() || undefined
          : (form.scoreText || '').trim() || undefined;
      const payload: VendorAssessmentPayload = {
        vendorId,
        docType: form.docType,
        purpose: form.purpose,
        assessmentNo: (form.assessmentNo || '').trim() || undefined,
        assessmentDate: form.assessmentDate || undefined,
        title: (form.title || '').trim() || undefined,
        criteriaJson: JSON.stringify(criteria),
        scoreText,
        judgment: form.judgment,
        summary: (form.summary || '').trim() || undefined,
        evaluator: (form.evaluator || '').trim() || undefined,
        status: form.status,
        remark: (form.remark || '').trim() || undefined,
      };
      if (mode === 'edit' && editingId != null) {
        await updateVendorAssessment(editingId, payload);
      } else {
        await createVendorAssessment(payload);
      }
      cancelForm();
      await load();
    } catch {
      reportError(setError, '저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row: VendorAssessment) => {
    if (!window.confirm(`${vendorAssessmentDocTypeLabel(row.docType)} 문서를 삭제할까요?`)) {
      return;
    }
    try {
      setSaving(true);
      await deleteVendorAssessment(row.assessmentId);
      if (editingId === row.assessmentId) cancelForm();
      await load();
    } catch {
      reportError(setError, '삭제에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  if (mode === 'create' || mode === 'edit') {
    return (
      <div className={`vendor-assess vendor-assess--${docType.toLowerCase()}`}>
        <div className="vendor-assess__toolbar">
          <div>
            <h4>
              {mode === 'edit' ? '수정' : '등록'} · {vendorAssessmentDocTypeLabel(docType)}
            </h4>
            <p className="vendor-hint-inline">{vendorAssessmentDocHint(docType)}</p>
          </div>
          <button type="button" className="is-ghost" onClick={cancelForm}>
            목록으로
          </button>
        </div>
        {error ? <p className="vendor-assess__error">{error}</p> : null}

        <form className="vendor-assess__form" onSubmit={(e) => void handleSubmit(e)}>
          <section className="vendor-assess__block is-span-2">
            <h5>기본 정보</h5>
            <div className="vendor-assess__grid">
              <label>
                문서유형 *
                <select
                  value={form.docType}
                  onChange={(e) =>
                    void handleDocTypeChange(e.target.value as VendorAssessmentDocType)
                  }
                  disabled={mode === 'edit'}
                >
                  <option value="SURVEY">조사표</option>
                  <option value="EVALUATION">평가표</option>
                  <option value="REPORT">조사보고서</option>
                </select>
              </label>
              <label>
                구분
                <select
                  value={form.purpose}
                  onChange={(e) => patch('purpose', e.target.value as VendorAssessmentPurpose)}
                >
                  <option value="INITIAL">신규선정</option>
                  <option value="PERIODIC">정기</option>
                  <option value="REEVALUATION">재평가</option>
                </select>
              </label>
              <label>
                문서번호
                <input
                  value={form.assessmentNo}
                  onChange={(e) => patch('assessmentNo', e.target.value)}
                  placeholder={
                    docType === 'SURVEY'
                      ? 'VS-2026-001'
                      : docType === 'EVALUATION'
                        ? 'VE-2026-001'
                        : 'VR-2026-001'
                  }
                />
              </label>
              <label>
                {dateLabel(docType)}
                <input
                  type="date"
                  value={form.assessmentDate}
                  onChange={(e) => patch('assessmentDate', e.target.value)}
                />
              </label>
              <label className="is-span-2">
                제목
                <input
                  value={form.title}
                  onChange={(e) => patch('title', e.target.value)}
                  placeholder={defaultTitle(docType)}
                />
              </label>
              <label>
                {personLabel(docType)}
                <input
                  value={form.evaluator}
                  onChange={(e) => patch('evaluator', e.target.value)}
                />
              </label>
              <label>
                상태
                <select
                  value={form.status}
                  onChange={(e) => patch('status', e.target.value as VendorAssessmentStatus)}
                >
                  <option value="DRAFT">작성중</option>
                  <option value="COMPLETED">완료</option>
                </select>
              </label>
            </div>
          </section>

          {docType === 'SURVEY' && criteria.kind === 'SURVEY'
            ? renderSurveyBody(criteria, setCriteria, patch, form)
            : null}
          {docType === 'EVALUATION' && criteria.kind === 'EVALUATION'
            ? renderEvalBody(criteria, setCriteria, patch, form, evalScorePreview)
            : null}
          {docType === 'REPORT' && criteria.kind === 'REPORT'
            ? renderReportBody(criteria, setCriteria, patch, form)
            : null}

          <section className="vendor-assess__block is-span-2">
            <h5>비고</h5>
            <div className="vendor-assess__grid">
              <label className="is-span-2">
                비고
                <textarea
                  rows={2}
                  value={form.remark}
                  onChange={(e) => patch('remark', e.target.value)}
                />
              </label>
            </div>
          </section>

          <div className="vendor-assess__actions is-span-2">
            <button type="button" className="is-ghost" onClick={cancelForm}>
              취소
            </button>
            <button type="submit" className="is-primary" disabled={saving}>
              {saving ? '저장 중...' : '저장'}
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="vendor-assess">
      <div className="vendor-assess__toolbar">
        <div className="vendor-assess__filters">
          {(
            [
              { key: 'ALL', label: '전체' },
              { key: 'SURVEY', label: '조사표' },
              { key: 'EVALUATION', label: '평가표' },
              { key: 'REPORT', label: '조사보고서' },
            ] as const
          ).map((item) => (
            <button
              key={item.key}
              type="button"
              className={docFilter === item.key ? 'is-active' : undefined}
              onClick={() => setDocFilter(item.key)}
            >
              {item.label}
            </button>
          ))}
        </div>
        <div className="vendor-assess__create-group">
          <button type="button" className="is-ghost" onClick={() => void openCreate('SURVEY')}>
            조사표
          </button>
          <button type="button" className="is-ghost" onClick={() => void openCreate('EVALUATION')}>
            평가표
          </button>
          <button type="button" className="is-primary" onClick={() => void openCreate('REPORT')}>
            보고서
          </button>
        </div>
      </div>

      {error ? <p className="vendor-assess__error">{error}</p> : null}

      {loading ? (
        <p className="vendor-hint-inline">불러오는 중...</p>
      ) : rows.length === 0 ? (
        <p className="vendor-hint-inline">
          등록된 조사/평가 문서가 없습니다. 상단에서 조사표·평가표·보고서를 추가하세요.
        </p>
      ) : (
        <table className="vendor-trade-table">
          <thead>
            <tr>
              <th>유형</th>
              <th>번호</th>
              <th>일자</th>
              <th>제목</th>
              <th>구분</th>
              <th>점수/결과</th>
              <th>판정</th>
              <th>상태</th>
              <th>담당</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.assessmentId}>
                <td>
                  <span className={`vendor-assess__type is-${String(row.docType).toLowerCase()}`}>
                    {vendorAssessmentDocTypeLabel(row.docType)}
                  </span>
                </td>
                <td>{row.assessmentNo || '-'}</td>
                <td>{row.assessmentDate?.slice(0, 10) || '-'}</td>
                <td>
                  <button
                    type="button"
                    className="vendor-assess__link"
                    onClick={() => openEdit(row)}
                  >
                    {row.title || '(제목 없음)'}
                  </button>
                </td>
                <td>{vendorAssessmentPurposeLabel(row.purpose)}</td>
                <td>{row.scoreText || (row.docType === 'REPORT' ? '서술형' : '-')}</td>
                <td>{vendorAssessmentJudgmentLabel(row.judgment)}</td>
                <td>
                  <span
                    className={`vendor-panel__badge ${
                      row.status === 'COMPLETED' ? 'is-active' : 'is-suspended'
                    }`}
                  >
                    {vendorAssessmentStatusLabel(row.status)}
                  </span>
                </td>
                <td>{row.evaluator || '-'}</td>
                <td className="vendor-assess__row-actions">
                  <button type="button" className="is-ghost" onClick={() => openEdit(row)}>
                    수정
                  </button>
                  <button
                    type="button"
                    className="is-danger"
                    disabled={saving}
                    onClick={() => void handleDelete(row)}
                  >
                    삭제
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function defaultSurveyLike() {
  return defaultCriteriaForDocType('SURVEY');
}

function renderSurveyBody(
  criteria: VendorSurveyCriteria,
  setCriteria: (next: VendorAssessmentCriteria) => void,
  patch: <K extends keyof FormState>(key: K, value: FormState[K]) => void,
  form: FormState
) {
  const updateItem = (idx: number, next: Partial<VendorSurveyItem>) => {
    setCriteria({
      ...criteria,
      items: criteria.items.map((item, i) => (i === idx ? { ...item, ...next } : item)),
    });
  };

  return (
    <>
      <section className="vendor-assess__block is-span-2">
        <h5>조사 개요</h5>
        <div className="vendor-assess__grid">
          <label>
            조사방법
            <select
              value={criteria.method}
              onChange={(e) => setCriteria({ ...criteria, method: e.target.value })}
            >
              <option value="VISIT">방문조사</option>
              <option value="DOCUMENT">서면조사</option>
              <option value="REMOTE">비대면(화상)</option>
            </select>
          </label>
          <label>
            조사장소
            <input
              value={criteria.visitPlace}
              onChange={(e) => setCriteria({ ...criteria, visitPlace: e.target.value })}
              placeholder="사업장/공장 주소 또는 회의명"
            />
          </label>
        </div>
      </section>

      <section className="vendor-assess__block is-span-2">
        <div className="vendor-assess__block-head">
          <h5>조사 항목</h5>
          <button
            type="button"
            className="is-ghost"
            onClick={() =>
              setCriteria({
                ...criteria,
                items: [
                  ...criteria.items,
                  {
                    id: newItemId(),
                    category: '',
                    question: '',
                    result: '',
                    note: '',
                  },
                ],
              })
            }
          >
            항목 추가
          </button>
        </div>
        <div className="vendor-assess__table-wrap">
          <table className="vendor-assess__table">
            <thead>
              <tr>
                <th className="is-cat">구분</th>
                <th>확인 내용</th>
                <th className="is-result">결과</th>
                <th className="is-note">비고</th>
                <th className="is-actions" />
              </tr>
            </thead>
            <tbody>
              {criteria.items.map((item, idx) => (
                <tr key={item.id}>
                  <td>
                    <input
                      value={item.category}
                      onChange={(e) => updateItem(idx, { category: e.target.value })}
                      placeholder="품질"
                    />
                  </td>
                  <td>
                    <input
                      value={item.question}
                      onChange={(e) => updateItem(idx, { question: e.target.value })}
                      placeholder="확인 질문"
                    />
                  </td>
                  <td>
                    <select
                      value={item.result}
                      onChange={(e) =>
                        updateItem(idx, {
                          result: e.target.value as VendorSurveyItem['result'],
                        })
                      }
                    >
                      <option value="">선택</option>
                      <option value="Y">적합</option>
                      <option value="N">미흡</option>
                      <option value="NA">해당없음</option>
                    </select>
                  </td>
                  <td>
                    <input
                      value={item.note}
                      onChange={(e) => updateItem(idx, { note: e.target.value })}
                    />
                  </td>
                  <td>
                    <button
                      type="button"
                      className="is-danger"
                      onClick={() =>
                        setCriteria({
                          ...criteria,
                          items: criteria.items.filter((_, i) => i !== idx),
                        })
                      }
                    >
                      삭제
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="vendor-assess__block is-span-2">
        <h5>조사 종합</h5>
        <div className="vendor-assess__grid">
          <label>
            종합 판정
            <select
              value={form.judgment}
              onChange={(e) => patch('judgment', e.target.value as VendorAssessmentJudgment)}
            >
              <option value="PENDING">판정대기</option>
              <option value="APPROVED">적합</option>
              <option value="CONDITIONAL">조건부</option>
              <option value="REJECTED">부적합</option>
            </select>
          </label>
          <label className="is-span-2">
            조사 소견
            <textarea
              rows={3}
              value={form.summary}
              onChange={(e) => patch('summary', e.target.value)}
              placeholder="현장 확인 결과, 특이사항, 추가 확인 필요 사항"
            />
          </label>
        </div>
      </section>
    </>
  );
}

function renderEvalBody(
  criteria: VendorEvalCriteria,
  setCriteria: (next: VendorAssessmentCriteria) => void,
  patch: <K extends keyof FormState>(key: K, value: FormState[K]) => void,
  form: FormState,
  scorePreview: string
) {
  const updateItem = (idx: number, next: Partial<VendorEvalItem>) => {
    setCriteria({
      ...criteria,
      items: criteria.items.map((item, i) => (i === idx ? { ...item, ...next } : item)),
    });
  };

  return (
    <>
      <section className="vendor-assess__block is-span-2">
        <div className="vendor-assess__block-head">
          <h5>평가 항목 (배점)</h5>
          <button
            type="button"
            className="is-ghost"
            onClick={() =>
              setCriteria({
                ...criteria,
                items: [
                  ...criteria.items,
                  {
                    id: newItemId(),
                    category: '',
                    criterion: '',
                    maxScore: 10,
                    score: '',
                    note: '',
                  },
                ],
              })
            }
          >
            항목 추가
          </button>
        </div>
        <div className="vendor-assess__table-wrap">
          <table className="vendor-assess__table">
            <thead>
              <tr>
                <th className="is-cat">구분</th>
                <th>평가 기준</th>
                <th className="is-max">배점</th>
                <th className="is-score">점수</th>
                <th className="is-note">의견</th>
                <th className="is-actions" />
              </tr>
            </thead>
            <tbody>
              {criteria.items.map((item, idx) => (
                <tr key={item.id}>
                  <td>
                    <input
                      value={item.category}
                      onChange={(e) => updateItem(idx, { category: e.target.value })}
                    />
                  </td>
                  <td>
                    <input
                      value={item.criterion}
                      onChange={(e) => updateItem(idx, { criterion: e.target.value })}
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      min={0}
                      value={item.maxScore}
                      onChange={(e) =>
                        updateItem(idx, { maxScore: Math.max(0, Number(e.target.value) || 0) })
                      }
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      min={0}
                      max={item.maxScore}
                      value={item.score}
                      onChange={(e) => updateItem(idx, { score: e.target.value })}
                    />
                  </td>
                  <td>
                    <input
                      value={item.note}
                      onChange={(e) => updateItem(idx, { note: e.target.value })}
                    />
                  </td>
                  <td>
                    <button
                      type="button"
                      className="is-danger"
                      onClick={() =>
                        setCriteria({
                          ...criteria,
                          items: criteria.items.filter((_, i) => i !== idx),
                        })
                      }
                    >
                      삭제
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="vendor-assess__score-preview">
          합산 점수: <strong>{scorePreview || '점수 입력 후 자동 계산'}</strong>
        </p>
      </section>

      <section className="vendor-assess__block is-span-2">
        <h5>평가 종합</h5>
        <div className="vendor-assess__grid">
          <label>
            점수/등급
            <input value={scorePreview || form.scoreText || ''} readOnly placeholder="자동 계산" />
          </label>
          <label>
            종합 판정
            <select
              value={form.judgment}
              onChange={(e) => patch('judgment', e.target.value as VendorAssessmentJudgment)}
            >
              <option value="PENDING">판정대기</option>
              <option value="APPROVED">적합</option>
              <option value="CONDITIONAL">조건부</option>
              <option value="REJECTED">부적합</option>
            </select>
          </label>
          <label className="is-span-2">
            평가 의견
            <textarea
              rows={3}
              value={form.summary}
              onChange={(e) => patch('summary', e.target.value)}
              placeholder="강점, 개선 필요 사항, 거래 계속/중단 의견"
            />
          </label>
        </div>
      </section>
    </>
  );
}

function renderReportBody(
  criteria: VendorReportCriteria,
  setCriteria: (next: VendorAssessmentCriteria) => void,
  patch: <K extends keyof FormState>(key: K, value: FormState[K]) => void,
  form: FormState
) {
  const patchCriteria = <K extends keyof VendorReportCriteria>(
    key: K,
    value: VendorReportCriteria[K]
  ) => setCriteria({ ...criteria, [key]: value });

  return (
    <>
      <section className="vendor-assess__block is-span-2">
        <h5>관련 문서</h5>
        <div className="vendor-assess__grid">
          <label>
            관련 조사표 번호
            <input
              value={criteria.relatedSurveyNo}
              onChange={(e) => patchCriteria('relatedSurveyNo', e.target.value)}
              placeholder="VS-2026-001"
            />
          </label>
          <label>
            관련 평가표 번호
            <input
              value={criteria.relatedEvalNo}
              onChange={(e) => patchCriteria('relatedEvalNo', e.target.value)}
              placeholder="VE-2026-001"
            />
          </label>
        </div>
      </section>

      <section className="vendor-assess__block is-span-2">
        <h5>보고서 본문</h5>
        <div className="vendor-assess__grid">
          <label className="is-span-2">
            조사 배경 · 목적
            <textarea
              rows={3}
              value={criteria.background}
              onChange={(e) => patchCriteria('background', e.target.value)}
              placeholder="신규 거래 검토, 정기 재평가, 품질이슈 조사 등"
            />
          </label>
          <label className="is-span-2">
            조사 방법 · 범위
            <textarea
              rows={2}
              value={criteria.method}
              onChange={(e) => patchCriteria('method', e.target.value)}
              placeholder="방문일, 면담자, 확인 자료, 점검 범위"
            />
          </label>
          <label className="is-span-2">
            조사 결과 · 주요 발견사항
            <textarea
              rows={4}
              value={criteria.findings}
              onChange={(e) => patchCriteria('findings', e.target.value)}
              placeholder="강점, 미흡 사항, 리스크"
            />
          </label>
          <label className="is-span-2">
            종합 결론
            <textarea
              rows={3}
              value={criteria.conclusion}
              onChange={(e) => patchCriteria('conclusion', e.target.value)}
              placeholder="거래 적격 여부 및 근거"
            />
          </label>
          <label className="is-span-2">
            후속 조치
            <textarea
              rows={2}
              value={criteria.followUp}
              onChange={(e) => patchCriteria('followUp', e.target.value)}
              placeholder="시정요청, 재방문, 조건부 승인 조건 등"
            />
          </label>
        </div>
      </section>

      <section className="vendor-assess__block is-span-2">
        <h5>보고 종합</h5>
        <div className="vendor-assess__grid">
          <label>
            최종 판정
            <select
              value={form.judgment}
              onChange={(e) => patch('judgment', e.target.value as VendorAssessmentJudgment)}
            >
              <option value="PENDING">판정대기</option>
              <option value="APPROVED">적합</option>
              <option value="CONDITIONAL">조건부</option>
              <option value="REJECTED">부적합</option>
            </select>
          </label>
          <label className="is-span-2">
            한줄 요약
            <textarea
              rows={2}
              value={form.summary}
              onChange={(e) => patch('summary', e.target.value)}
              placeholder="목록·내부심사에서 바로 볼 수 있는 요약"
            />
          </label>
        </div>
      </section>
    </>
  );
}

export default VendorAssessmentSection;
